/**
 * Validação de formulário que FALA — o padrão único da rodada V-1.
 *
 * **O bug que originou isto.** A auditoria interativa (2026-08-01) achou oito
 * modais em que clicar no botão primário com o formulário vazio não produzia
 * nada: sem mensagem, sem foco, sem validação nativa. A causa não era falta de
 * validação — era `disabled={!valid}`. A regra existia; o que ela comunicava era
 * um **botão morto**.
 *
 * Botão desabilitado é uma resposta muda: diz "não pode" e nunca diz "por quê".
 * Quem preencheu oito campos e esqueceu um fica olhando para um botão apagado
 * sem saber qual deles falta — e, pior, sem certeza de que o problema é ele
 * (pode ser lentidão, pode ser permissão, pode ser bug).
 *
 * A troca:
 *   1. o botão fica **habilitado**;
 *   2. clicar **valida** e, falhando, aponta o PRIMEIRO campo que falhou —
 *      mensagem embaixo dele **e foco nele**;
 *   3. um aviso geral acompanha quando ajuda (a escada de `camadas.ts` garante
 *      que ele apareça por cima de qualquer diálogo);
 *   4. enquanto salva, o botão mostra que está salvando.
 *
 * Regra do `CLAUDE.md` (formulário nunca é surdo): toda ação de submeter
 * responde. Silêncio é o pior resultado possível.
 */
import { useCallback, useState } from 'react';

export interface RegraCampo {
  /** `id` do input — é por ele que o foco vai para o campo certo. */
  id: string;
  /** `false` = falhou. Escreva a condição do jeito positivo ("tem nome"). */
  ok: boolean;
  /** O que dizer. Em português, dizendo o que fazer — não "campo inválido". */
  mensagem: string;
}

export interface ValidacaoForm {
  /** Mensagem do campo, se ele falhou na última tentativa. */
  erroDe: (id: string) => string | undefined;
  /**
   * Roda as regras na ORDEM em que aparecem — a primeira que falhar é a que
   * recebe foco e mensagem. Ordem das regras = ordem visual dos campos, senão o
   * foco pula para um campo que a pessoa ainda nem chegou.
   *
   * Devolve `true` quando pode seguir.
   */
  validar: (regras: RegraCampo[]) => boolean;
  /** Limpa os erros (ao fechar o modal, ao trocar de aba do wizard). */
  limpar: () => void;
}

export function useValidacaoForm(
  aoFalhar?: (mensagem: string) => void,
): ValidacaoForm {
  const [erros, setErros] = useState<Record<string, string>>({});

  const validar = useCallback(
    (regras: RegraCampo[]) => {
      const falha = regras.find((r) => !r.ok);
      if (!falha) {
        setErros({});
        return true;
      }
      setErros({ [falha.id]: falha.mensagem });

      // O foco é metade da correção: sem ele, a pessoa lê a mensagem e ainda
      // precisa caçar o campo numa tela cheia. `setTimeout` porque o React ainda
      // não pintou a mensagem quando esta linha roda.
      setTimeout(() => {
        const el = document.getElementById(falha.id);
        if (!el) return;
        el.focus();
        // `scrollIntoView` não existe em todo ambiente — o jsdom do CI não
        // implementa, e chamar direto derrubava 6 suítes de tela com
        // "el.scrollIntoView is not a function". Passava na minha máquina e
        // quebrava lá: o guard existe por isso, e porque a hierarquia aqui é
        // clara — o FOCO é o que corrige o problema do usuário; rolar até o
        // campo é conforto. Perder o conforto não pode derrubar a correção.
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 0);

      aoFalhar?.(falha.mensagem);
      return false;
    },
    [aoFalhar],
  );

  const limpar = useCallback(() => setErros({}), []);
  const erroDe = useCallback((id: string) => erros[id], [erros]);

  return { erroDe, validar, limpar };
}
